import { QueryClient, VueQueryPlugin, hydrate } from '@tanstack/vue-query'

import { createApp as createClientSideApp, createSSRApp } from 'vue'
import { RouterHistory, createWebHashHistory, createWebHistory } from 'vue-router'

import App from '@/App.vue'
import { isIOSPWA, isPWARuntime } from '@/components_new/PWAInstall/utils'
import { createConfigPlugin } from '@/composables/config'
import { createI18nWithAddon } from '@/i18n/i18n'
import createPinia from '@/pinia'
import createRouter from '@/router'
import { VueHeadMixin, createHead } from '@/utils/head/client'

import { createConfigLocalizedRouters } from './composables/localized-routers'
import { resolveSupportedLocale } from './i18n/helpers'
import { DEFAULT_QUERIES_OPTIONS } from './query/shared'
import { PWA_CLID } from './utils/affiliate'
import { getCurrentLocaleFromCookies } from './utils/cookie-parser'
import { idleCallbackPromise } from './utils/helpers'
import { setupCrashTracking } from './utils/reporting'
import { createApiRequestsPlugin } from './utils/requests/api-plugin'
import { ApiRequests } from './utils/requests/api-requests'
import { fetchRequest } from './utils/requests/fetch-requests'

const run = async () => {
    // Necessary to init router before setting crash tracking, otherwise vue router bugs
    // https://github.com/vuejs/router/issues/366
    let routerHistory: RouterHistory
    if (isIOSPWA()) {
        // we use hash history for IOS PWA to keep the URL same and not cause browser UI to appear
        routerHistory = createWebHashHistory()
        routerHistory.push(`${location.pathname}${location.search}`)
    } else {
        routerHistory = createWebHistory()
    }

    setupCrashTracking()

    await idleCallbackPromise()

    let appConfig = window.additionalServerData?.config

    // this is to simulate SSR redirects in development
    let detectedLocale = window.additionalServerData?.locale.detectedLocale

    if (process.env.DEV_CONFIG) {
        appConfig = (await import('@/modules/config/createConfig')).createVueAppConfig(process.env.DEV_CONFIG)

        // to test specific locale you can hardcode it to any lang-code in BCP47 standard format
        const locale = getCurrentLocaleFromCookies(document.cookie)
        const resolvedLocale = resolveSupportedLocale(locale)
        if (resolvedLocale) {
            detectedLocale = resolvedLocale
        }
    }
    if (!appConfig) {
        throw new Error('No app config provided')
    }

    const apiPlugin = createApiRequestsPlugin(new ApiRequests({ request: fetchRequest(appConfig.API_HOST) }))
    const configPlugin = createConfigPlugin(appConfig)
    const head = createHead()

    const { i18nAddon } = createI18nWithAddon({
        enableI18n: appConfig.VUE_APP_ENABLE_I18N,
        detectedLocale,
    })

    if (isPWARuntime()) {
        const query = new URLSearchParams(location.search)
        if (!query.get('clid')) {
            query.set('clid', PWA_CLID)
            routerHistory.push(`${location.pathname}?${query.toString()}`)
        }
    }
    let resolveRouterInit = () => {}
    const deferredRouterPromise = new Promise<void>((resolve) => {
        resolveRouterInit = () => resolve()
    })
    const { router, routersByLocale } = createRouter({
        history: routerHistory,
        i18nAddon,
        registerImport: (x) => () => x(),
        deferredInit: deferredRouterPromise,
    })
    const localizedRoutersPlugin = createConfigLocalizedRouters(routersByLocale)
    const pinia = createPinia()
    pinia.state.value = window.__pinia || {}

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: DEFAULT_QUERIES_OPTIONS,
        },
    })
    if (window.__query) {
        hydrate(queryClient, window.__query)
    }
    await idleCallbackPromise()

    const createApp = process.env.NODE_ENV === 'production' ? createSSRApp : createClientSideApp
    const app = createApp(App)
        .use(configPlugin)
        .use(apiPlugin)
        .use(i18nAddon)
        .use(router)
        .use(localizedRoutersPlugin)
        .use(pinia)
        .use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: true })
        .use(head)
        .mixin(VueHeadMixin)

    app.config.performance = true

    await router.isReady()

    await idleCallbackPromise()

    app.mount('#app')
    resolveRouterInit()
}

run()
